import type { ComponentProps, ComponentPropsWithoutRef, ReactNode } from 'react';
import { ActionButton } from './ActionButton.js';
import '../find/search.css';
import '../records/records.css';
import '../company/company.css';

const classes = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

export type OptionButtonProps = Omit<ComponentProps<typeof ActionButton>, 'reading'> & {
  variant?: 'lead' | 'plain' | 'small';
  /** Only reads/opens something. Omit for any action that can write, including opening a reply composer. */
  opensOnly?: boolean;
  number?: ReactNode;
  note?: ReactNode;
};

/** ItemCard's U28–U43 option, with its existing number/label/note markup. */
export function OptionButton({ variant = 'plain', opensOnly = false, number, note, className, children, ...props }: OptionButtonProps) {
  return <ActionButton type="button" {...props} reading={opensOnly} className={classes('opt', variant !== 'plain' && variant, className)}>
    {number != null ? <span className="n">{number}</span> : null}<span className="l">{children}</span>{note ? <small>{note}</small> : null}
  </ActionButton>;
}

export type RowAction = Omit<OptionButtonProps, 'children'> & { key: string; label: ReactNode };
/** The caller keeps the approved offer order and words. Reply/Reply all, options and standing actions have different jobs. */
export function ActionRow({ actions, variant = 'options', className, ...props }: Omit<ComponentProps<'div'>, 'children'> & {
  actions: readonly RowAction[]; variant?: 'options' | 'replies' | 'standing';
}) {
  return <div {...props} className={classes(variant === 'options' ? 'opts' : variant === 'replies' ? 'card-replies' : 'standing', className)}>
    {actions.map(({ key, label, ...action }) => variant === 'options' ? <OptionButton key={key} {...action}>{label}</OptionButton> :
      <RowButton key={key} action={action} variant={variant}>{label}</RowButton>)}
  </div>;
}
function RowButton({ action: { opensOnly, variant: _variant, number: _number, note: _note, className, ...props }, variant, children }: {
  action: Omit<RowAction, 'key' | 'label'>; variant: 'replies' | 'standing'; children: ReactNode;
}) {
  return <ActionButton type="button" {...props} reading={opensOnly ?? false} className={classes(variant === 'replies' && 'opt', className)}>{children}</ActionButton>;
}

/** ItemCard's article, or the existing evidence panel frame. Page-specific contents stay with the page. */
export function CardFrame({ variant = 'triage', current = false, private: personal = false, className, ...props }: ComponentPropsWithoutRef<'article'> & {
  variant?: 'triage' | 'panel'; current?: boolean; private?: boolean;
}) {
  const cls = classes(variant === 'triage' ? 'item' : 'panel', current && 'current', personal && 'private', className);
  return variant === 'triage' ? <article {...props} className={cls} /> : <div {...props} className={cls} />;
}

/** Triage's .tierh: the heading and its note are siblings, in that order. */
export function SectionHeading({ title, note, className, ...props }: Omit<ComponentProps<'div'>, 'title'> & { title: ReactNode; note?: ReactNode }) {
  return <div {...props} className={classes('tierh', className)}><h2>{title}</h2>{note ? <span>{note}</span> : null}</div>;
}

/** Search's list grammar (U227), also used by Everything read. */
export function List({ className, ...props }: ComponentProps<'ul'>) { return <ul {...props} className={classes('search-list', className)} />; }
export function ListRow({ private: personal = false, className, ...props }: ComponentProps<'li'> & { private?: boolean }) {
  return <li {...props} className={classes('search-row', personal && 'private', className)} />;
}

/** An informational pill, or the company-kind chip from CompanyLine (U353). Actions use OptionButton. */
export function Chip({ variant = 'plain', className, ...props }: ComponentProps<'span'> & { variant?: 'plain' | 'private' | 'back' | 'company' }) {
  return <span {...props} className={classes(variant === 'company' ? 'cl-chip' : 'pill', (variant === 'private' || variant === 'back') && variant, className)} />;
}

/** records/shared.tsx Empty, without the table wrapper when used outside a table. No default words. */
export function EmptyState({ title, note, columns, ...props }: Omit<ComponentProps<'div'>, 'title'> & { title: ReactNode; note: ReactNode; columns?: number }) {
  const content = <div {...props} className={classes('rec-empty', props.className)}><h3>{title}</h3><p>{note}</p></div>;
  return columns ? <tr><td colSpan={columns} style={{ padding: 0 }}>{content}</td></tr> : content;
}

/** Triage's U34 strip. The owner keeps the timer/undo operation; this preserves the markup and locks the write. */
export function UndoStrip({ words, rose, onRose, onUndo, ...props }: Omit<ComponentProps<'div'>, 'children'> & {
  words: ReactNode; rose?: ReactNode; onRose?: () => void; onUndo?: (() => void) | undefined;
}) {
  return <div role="status" aria-live="polite" {...props} className={classes('undo', !!words && 'on', props.className)}>
    {words ? <><span className="undo-words"><span>{words}</span>{rose ? (onRose
      ? <ActionButton reading type="button" className="undo-rose" onClick={onRose}>{rose}</ActionButton>
      : <span className="undo-rose">{rose}</span>) : null}</span>
    {onUndo ? <ActionButton type="button" onClick={onUndo}>Undo <kbd className="undo-key">Z</kbd></ActionButton> : null}</> : null}
  </div>;
}
